/**
 * Guide Me - action executors.
 *
 * Each action takes a small `ActionContext` (the player's view) and a step,
 * and either performs the action or - when `dryRun` blocks it - returns a
 * narration so the player can prompt the user to take over.
 */

import {
  DESTRUCTIVE_NAME_PATTERN,
  type AnnotateStep,
  type Assertion,
  type ChapterStep,
  type CheckStep,
  type ClickStep,
  type DemoMode,
  type DragStep,
  type ExpectStep,
  type HighlightStep,
  type HoverStep,
  type Locator,
  type MoveStep,
  type MultiSpotStep,
  type NarrateStep,
  type NavigateStep,
  type PressStep,
  type ScrollToStep,
  type SelectStep,
  type Step,
  type TourPace,
  type TypeStep,
  type UncheckStep,
  type WaitForStep,
  type WaitStep,
} from './types';
import {
  accessibleName,
  describeLocator,
  isVisible,
  resolveAll,
  resolveOne,
  waitForLocator,
} from './locators';

export interface CursorPosition {
  x: number;
  y: number;
}

export interface ActionContext {
  /** Per-step timeout for locator resolution / waitFor. */
  defaultTimeoutMs: number;
  /** Player's demo mode. dry-run blocks the final destructive click. */
  demoMode: DemoMode;
  /** True when the user has the OS "reduce motion" preference set. */
  reducedMotion: boolean;
  /** Tour-wide pace overrides. Per-step fields take priority over these. */
  pace: TourPace;
  /** Current SPA pathname from React Router — used to skip redundant navigate steps. */
  currentPathname: string;
  /** Read the current cursor position (in viewport pixels). */
  getCursorPos(): CursorPosition;
  /** Move the virtual cursor (with animation) to a target point. */
  moveCursor(target: CursorPosition, opts?: { durationMs?: number }): Promise<void>;
  /** Brief click ripple at the cursor's current position. */
  pulseCursor(): void;
  /** Update the spotlight ring position. */
  setSpotlight(target: HTMLElement | null): void;
  /**
   * Update the on-screen narration bubble.
   * `stepType` is forwarded to the log sidebar for icons/colouring.
   */
  setNarration(text: string, stepType?: string): void;
  /**
   * Update the current chapter/section name shown on the spotlight card.
   * Called by `chapter` steps; pass `null` to clear back to the tour title.
   */
  setChapter?(title: string | null): void;
  /** Programmatic SPA navigation (react-router). */
  navigate(to: string): void;
  /** Sleep for `ms` ms unless the run is cancelled. */
  sleep(ms: number): Promise<void>;
  /** Throws a CancellationError when the player has been exited. */
  checkCancelled(): void;
}

export class ActionAbortError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ActionAbortError';
  }
}

export class CancellationError extends Error {
  constructor() {
    super('guide-me run was cancelled');
    this.name = 'CancellationError';
  }
}

/**
 * Raised by an `expect` step whose assertion did not hold. Treated by
 * the lifecycle wrapper as recoverable (retry-eligible); only after
 * `step.retry` attempts are exhausted does it propagate up to
 * {@link TourPlayer}'s catch chain, which surfaces it to the user as
 * "Check failed: …".
 *
 * `softFail` carries the per-assertion override — when true the
 * runtime narrates the failure and continues rather than aborting the
 * tour.
 */
export class AssertionError extends Error {
  readonly softFail: boolean;
  readonly detail?: { actual?: unknown; expected?: unknown; locator?: Locator };
  constructor(
    message: string,
    detail?: { actual?: unknown; expected?: unknown; locator?: Locator },
    softFail = false,
  ) {
    super(message);
    this.name = 'AssertionError';
    this.softFail = softFail;
    this.detail = detail;
  }
}

/* -------------------------------------------------------------------------- */
/* Helpers                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Pacing defaults. Tuned so the user can read the narration and watch the
 * cursor without feeling rushed. Tour authors override per-step via
 * `perCharMs` / `settleMs`, or tour-wide via `tour.pace`.
 */
const DEFAULT_TYPE_PER_CHAR_MS = 110;
const DEFAULT_NARRATE_HOLD_MS = 2800;

function centerOf(el: HTMLElement): CursorPosition {
  const rect = el.getBoundingClientRect();
  return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
}

async function moveAndSpotlight(ctx: ActionContext, el: HTMLElement, durationMs?: number): Promise<void> {
  ensureInView(el);
  ctx.setSpotlight(el);
  await ctx.moveCursor(centerOf(el), { durationMs });
}

function ensureInView(el: HTMLElement): void {
  const rect = el.getBoundingClientRect();
  const viewportH = window.innerHeight || document.documentElement.clientHeight;
  const viewportW = window.innerWidth || document.documentElement.clientWidth;
  if (rect.top < 80 || rect.bottom > viewportH - 80 || rect.left < 0 || rect.right > viewportW) {
    // jsdom doesn't implement scrollIntoView; guard so unit tests
    // running under jsdom don't blow up on visual-positioning steps.
    if (typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
    }
  }
}

async function resolve(ctx: ActionContext, locator: Locator, timeoutMs: number): Promise<HTMLElement> {
  const direct = resolveOne(locator);
  if (direct) return direct;
  const found = await waitForLocator(locator, timeoutMs);
  if (!found) {
    throw new ActionAbortError(`could not find ${describeLocator(locator)} within ${timeoutMs}ms`);
  }
  ctx.checkCancelled();
  return found;
}

/** Find the actual focusable input/textarea inside a wrapping element (TextField wrapper, etc). */
function focusableInside(el: HTMLElement): HTMLElement {
  if (el.matches('input, textarea, select, [contenteditable="true"]')) return el;
  const inner = el.querySelector<HTMLElement>(
    'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), select:not([disabled]), [contenteditable="true"]',
  );
  return inner ?? el;
}

/** Set the value of a controlled React input so React updates its state. */
function setReactValue(input: HTMLElement, value: string): void {
  if (input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement) {
    const proto = input instanceof HTMLInputElement ? HTMLInputElement.prototype : HTMLTextAreaElement.prototype;
    const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
    if (setter) {
      setter.call(input, value);
    } else {
      input.value = value;
    }
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  } else if (input instanceof HTMLSelectElement) {
    const setter = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, 'value')?.set;
    if (setter) setter.call(input, value); else input.value = value;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  } else if (input.isContentEditable) {
    input.textContent = value;
    input.dispatchEvent(new InputEvent('input', { bubbles: true }));
  }
}

function dispatchClickSequence(el: HTMLElement): void {
  const opts: MouseEventInit = { bubbles: true, cancelable: true, view: window, button: 0 };
  el.dispatchEvent(new MouseEvent('mousedown', opts));
  el.dispatchEvent(new MouseEvent('mouseup', opts));
  el.dispatchEvent(new MouseEvent('click', opts));
}

function dispatchHover(el: HTMLElement): void {
  const opts: MouseEventInit = { bubbles: true, cancelable: true, view: window };
  el.dispatchEvent(new MouseEvent('mouseover', opts));
  el.dispatchEvent(new MouseEvent('mouseenter', { ...opts, bubbles: false }));
  el.dispatchEvent(new MouseEvent('mousemove', opts));
}

function isDestructive(el: HTMLElement): boolean {
  return DESTRUCTIVE_NAME_PATTERN.test(accessibleName(el));
}

/* -------------------------------------------------------------------------- */
/* Per-step executors                                                          */
/* -------------------------------------------------------------------------- */

async function runNarrate(ctx: ActionContext, step: NarrateStep): Promise<void> {
  ctx.setNarration(step.narrate ?? step.text, 'narrate');
  await ctx.sleep(step.timeoutMs ?? ctx.pace.narrateMs ?? DEFAULT_NARRATE_HOLD_MS);
}

async function runNavigate(ctx: ActionContext, step: NavigateStep): Promise<void> {
  ctx.setNarration(step.narrate ?? `Going to ${step.to}`, 'navigate');
  // Use the React Router pathname (from ctx) to avoid re-navigating to the
  // current page, which causes the plugin to remount and looks like a refresh.
  const alreadyHere =
    ctx.currentPathname === step.to ||
    window.location.pathname === step.to;
  if (!alreadyHere) {
    ctx.navigate(step.to);
    await ctx.sleep(450);
  }
}

async function runScrollTo(ctx: ActionContext, step: ScrollToStep): Promise<void> {
  const timeout = step.timeoutMs ?? ctx.defaultTimeoutMs;
  const el = await resolve(ctx, step.locator, timeout);
  ctx.setNarration(step.narrate ?? `Scrolling to ${describeLocator(step.locator)}`, 'scrollTo');
  el.scrollIntoView({ behavior: ctx.reducedMotion ? 'auto' : 'smooth', block: 'center', inline: 'center' });
  await ctx.sleep(ctx.reducedMotion ? 0 : 400);
  await moveAndSpotlight(ctx, el, ctx.reducedMotion ? 0 : 700);
}

async function runMove(ctx: ActionContext, step: MoveStep): Promise<void> {
  const timeout = step.timeoutMs ?? ctx.defaultTimeoutMs;
  const el = await resolve(ctx, step.locator, timeout);
  ctx.setNarration(step.narrate ?? `Moving to ${describeLocator(step.locator)}`, 'move');
  await moveAndSpotlight(ctx, el);
}

async function runHover(ctx: ActionContext, step: HoverStep): Promise<void> {
  const timeout = step.timeoutMs ?? ctx.defaultTimeoutMs;
  const el = await resolve(ctx, step.locator, timeout);
  ctx.setNarration(step.narrate ?? `Hovering on ${describeLocator(step.locator)}`, 'hover');
  await moveAndSpotlight(ctx, el);
  dispatchHover(el);
  await ctx.sleep(500);
}

async function runClick(ctx: ActionContext, step: ClickStep): Promise<void> {
  const timeout = step.timeoutMs ?? ctx.defaultTimeoutMs;
  const el = await resolve(ctx, step.locator, timeout);
  await moveAndSpotlight(ctx, el);

  const blockedByDryRun = ctx.demoMode === 'dry-run' && isDestructive(el);
  const blockedByExecuteFlag = step.execute === false;

  if (blockedByExecuteFlag || blockedByDryRun) {
    const reason = blockedByDryRun
      ? `Stopped before "${accessibleName(el)}" so we don't change real data. Click it yourself when you're ready.`
      : (step.narrate ?? `Click "${accessibleName(el)}" to continue.`);
    ctx.setNarration(reason, 'handoff');
    dispatchHover(el);
    throw new HandoffSignal(reason);
  }

  ctx.setNarration(step.narrate ?? `Clicking "${accessibleName(el)}"`, 'click');
  ctx.pulseCursor();
  await ctx.sleep(200);
  dispatchClickSequence(el);
  await ctx.sleep(350);
}

async function runType(ctx: ActionContext, step: TypeStep): Promise<void> {
  const timeout = step.timeoutMs ?? ctx.defaultTimeoutMs;
  const wrapper = await resolve(ctx, step.locator, timeout);
  const input = focusableInside(wrapper);
  await moveAndSpotlight(ctx, input);

  if (step.execute === false) {
    ctx.setNarration(step.narrate ?? `Type "${step.text}" here yourself.`, 'handoff');
    throw new HandoffSignal(step.narrate ?? `Type "${step.text}" here yourself.`);
  }

  ctx.setNarration(step.narrate ?? `Typing "${step.text}"`, 'type');
  if (input instanceof HTMLElement && typeof (input as HTMLElement).focus === 'function') {
    input.focus();
  }
  if (step.clear !== false) {
    setReactValue(input, '');
  }
  const perChar =
    step.perCharMs ??
    (ctx.reducedMotion ? 0 : ctx.pace.perCharMs ?? DEFAULT_TYPE_PER_CHAR_MS);
  let buffer = '';
  for (const ch of step.text) {
    ctx.checkCancelled();
    buffer += ch;
    setReactValue(input, buffer);
    if (perChar > 0) await ctx.sleep(perChar);
  }
}

async function runPress(ctx: ActionContext, step: PressStep): Promise<void> {
  let target: HTMLElement = document.activeElement instanceof HTMLElement
    ? document.activeElement
    : (document.body as HTMLElement);
  if (step.locator) {
    target = await resolve(ctx, step.locator, step.timeoutMs ?? ctx.defaultTimeoutMs);
    target.focus();
  }
  ctx.setNarration(step.narrate ?? `Pressing ${step.key}`, 'press');
  const init: KeyboardEventInit = { key: step.key, bubbles: true, cancelable: true };
  target.dispatchEvent(new KeyboardEvent('keydown', init));
  target.dispatchEvent(new KeyboardEvent('keyup', init));
  await ctx.sleep(180);
}

async function runSelect(ctx: ActionContext, step: SelectStep): Promise<void> {
  const timeout = step.timeoutMs ?? ctx.defaultTimeoutMs;
  const wrapper = await resolve(ctx, step.locator, timeout);
  const input = focusableInside(wrapper);
  await moveAndSpotlight(ctx, input);

  if (input instanceof HTMLSelectElement && step.value !== undefined) {
    ctx.setNarration(step.narrate ?? `Selecting "${step.value}"`, 'select');
    setReactValue(input, step.value);
    return;
  }
  // Custom dropdown: open the trigger, then click the option by visible label.
  ctx.setNarration(step.narrate ?? `Choosing "${step.selectOption ?? step.value ?? ''}"`, 'select');
  ctx.pulseCursor();
  dispatchClickSequence(input);
  await ctx.sleep(350);

  const optionLabel = step.selectOption ?? step.value ?? '';
  const opt = await waitForLocator(
    { role: 'option', name: optionLabel, exact: false },
    timeout,
  );
  if (!opt) {
    throw new ActionAbortError(`option "${optionLabel}" did not appear`);
  }
  await moveAndSpotlight(ctx, opt);
  ctx.pulseCursor();
  dispatchClickSequence(opt);
  await ctx.sleep(280);
}

async function runWaitFor(ctx: ActionContext, step: WaitForStep): Promise<void> {
  const timeout = step.timeoutMs ?? ctx.defaultTimeoutMs;
  ctx.setNarration(step.narrate ?? `Waiting for ${describeLocator(step.locator)}`, 'waitFor');
  await resolve(ctx, step.locator, timeout);
}

async function runWait(ctx: ActionContext, step: WaitStep): Promise<void> {
  if (step.narrate) ctx.setNarration(step.narrate, 'wait');
  await ctx.sleep(step.ms);
}

/* -------------------------------------------------------------------------- */
/* expect (ETL v2)                                                             */
/* -------------------------------------------------------------------------- */

function readVisibleText(el: HTMLElement): string {
  return (el.textContent ?? '').replace(/\s+/g, ' ').trim();
}

function readInputValue(el: HTMLElement): string {
  if (
    el instanceof HTMLInputElement ||
    el instanceof HTMLTextAreaElement ||
    el instanceof HTMLSelectElement
  ) return el.value;
  return readVisibleText(el);
}

function isElementEnabled(el: HTMLElement): boolean {
  if (el.hasAttribute('disabled')) return false;
  const aria = el.getAttribute('aria-disabled');
  if (aria === 'true') return false;
  return true;
}

/**
 * Evaluates an {@link Assertion} against the live DOM. Returns `null`
 * when the assertion holds; otherwise returns a human-readable failure
 * message that the caller turns into an {@link AssertionError}.
 *
 * No retry / settle behavior here — the lifecycle wrapper owns those.
 * For locator-based assertions where the element may not exist yet
 * (`exists` / `visible`), we fall back to the synchronous `resolveOne`
 * because retries are how the runtime gives async UIs a chance to
 * render. See {@link executeStepWithLifecycle}.
 */
function evaluateAssertion(a: Assertion): string | null {
  switch (a.kind) {
    case 'exists': {
      return resolveOne(a.locator!) ? null : `expected ${describeLocator(a.locator!)} to exist`;
    }
    case 'not-exists': {
      return resolveOne(a.locator!) ? `expected ${describeLocator(a.locator!)} not to exist` : null;
    }
    case 'visible': {
      const el = resolveOne(a.locator!);
      return el && isVisible(el)
        ? null
        : `expected ${describeLocator(a.locator!)} to be visible`;
    }
    case 'not-visible': {
      const el = resolveOne(a.locator!);
      return el && isVisible(el)
        ? `expected ${describeLocator(a.locator!)} not to be visible`
        : null;
    }
    case 'enabled': {
      const el = resolveOne(a.locator!);
      if (!el) return `expected ${describeLocator(a.locator!)} to exist (and be enabled)`;
      return isElementEnabled(el) ? null : `expected ${describeLocator(a.locator!)} to be enabled`;
    }
    case 'disabled': {
      const el = resolveOne(a.locator!);
      if (!el) return `expected ${describeLocator(a.locator!)} to exist (and be disabled)`;
      return !isElementEnabled(el) ? null : `expected ${describeLocator(a.locator!)} to be disabled`;
    }
    case 'text':
    case 'value': {
      const el = resolveOne(a.locator!);
      if (!el) return `expected ${describeLocator(a.locator!)} to exist`;
      const actual = a.kind === 'text' ? readVisibleText(el) : readInputValue(el);
      const expected = String(a.expected ?? '');
      if (a.op === 'equals') {
        return actual === expected
          ? null
          : `expected ${a.kind} of ${describeLocator(a.locator!)} to equal "${expected}", got "${actual}"`;
      }
      if (a.op === 'contains') {
        return actual.includes(expected)
          ? null
          : `expected ${a.kind} of ${describeLocator(a.locator!)} to contain "${expected}", got "${actual}"`;
      }
      // 'matches' (regex) is reserved for a later phase.
      return `op "${a.op}" not yet supported for ${a.kind} assertions`;
    }
    case 'count': {
      const all = resolveAll(a.locator!);
      const actual = all.length;
      const expected = Number(a.expected ?? 0);
      const op = a.op ?? '==';
      const pass = (() => {
        switch (op) {
          case '==': return actual === expected;
          case '!=': return actual !== expected;
          case '<':  return actual <  expected;
          case '<=': return actual <= expected;
          case '>':  return actual >  expected;
          case '>=': return actual >= expected;
          default:   return false;
        }
      })();
      return pass
        ? null
        : `expected count(${describeLocator(a.locator!)}) ${op} ${expected}, got ${actual}`;
    }
    case 'expr':
      return 'expr assertions are reserved for a later ETL phase';
  }
}

/* -------------------------------------------------------------------------- */
/* highlight / check / uncheck / drag / multi-spot / annotate (ETL v2)         */
/* -------------------------------------------------------------------------- */

async function runHighlight(ctx: ActionContext, step: HighlightStep): Promise<void> {
  const timeout = step.timeoutMs ?? ctx.defaultTimeoutMs;
  const el = await resolve(ctx, step.locator, timeout);
  ensureInView(el);
  ctx.setSpotlight(el);
  if (step.narrate) ctx.setNarration(step.narrate, 'highlight');
  // Hold briefly so the highlight registers visually. The lifecycle
  // wrapper's settle pause handles the rest of the dwell time.
  await ctx.sleep(300);
}

/**
 * Sets a checkbox/radio's checked state via the React-internals
 * descriptor trick. Idempotent — `check` on an already-checked element
 * is a no-op (no extra change events fired); same for `uncheck`.
 */
async function runCheck(ctx: ActionContext, step: CheckStep | UncheckStep): Promise<void> {
  const timeout = step.timeoutMs ?? ctx.defaultTimeoutMs;
  const wrapper = await resolve(ctx, step.locator, timeout);
  const input = focusableInside(wrapper);
  await moveAndSpotlight(ctx, input);

  if (!(input instanceof HTMLInputElement) ||
      (input.type !== 'checkbox' && input.type !== 'radio')) {
    throw new ActionAbortError(
      `${step.type} expects a checkbox/radio input (got ${input.tagName.toLowerCase()})`,
    );
  }

  const desired = step.type === 'check';
  if (input.checked === desired) {
    ctx.setNarration(step.narrate ?? `${step.type}: already ${desired ? 'checked' : 'unchecked'}`, step.type);
    return;
  }

  const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'checked')?.set;
  if (setter) setter.call(input, desired);
  else input.checked = desired;
  input.dispatchEvent(new Event('input', { bubbles: true }));
  input.dispatchEvent(new Event('change', { bubbles: true }));
  ctx.setNarration(step.narrate ?? `${step.type === 'check' ? 'Checking' : 'Unchecking'} ${describeLocator(step.locator)}`, step.type);
  ctx.pulseCursor();
  await ctx.sleep(300);
}

/**
 * Drags one element onto another via a synthetic pointer sequence.
 * Fires the legacy MouseEvent path (dragstart/drag/dragover/drop/
 * dragend) — react-dnd and the HTML5 DnD API both observe these.
 * Pointer-API-only DnD libraries (used by some grid layouts) need a
 * pointer-event variant, which we emit alongside.
 */
async function runDrag(ctx: ActionContext, step: DragStep): Promise<void> {
  const timeout = step.timeoutMs ?? ctx.defaultTimeoutMs;
  const fromEl = await resolve(ctx, step.from, timeout);
  const toEl   = await resolve(ctx, step.to,   timeout);

  await moveAndSpotlight(ctx, fromEl);
  ctx.setNarration(
    step.narrate ?? `Dragging ${describeLocator(step.from)} to ${describeLocator(step.to)}`,
    'drag',
  );

  const fromCenter = centerOf(fromEl);
  const toCenter = centerOf(toEl);
  const transfer = (() => {
    try { return new DataTransfer(); } catch { return null; }
  })();

  const opts = (target: HTMLElement, x: number, y: number): MouseEventInit => ({
    bubbles: true,
    cancelable: true,
    view: window,
    button: 0,
    clientX: x,
    clientY: y,
    target,
  } as MouseEventInit);

  // Construct a DragEvent when the runtime supports it (every modern
  // browser does); fall back to a MouseEvent of the same name in
  // environments missing the constructor (jsdom, SSR mocks).
  const W = window as unknown as { DragEvent?: typeof DragEvent };
  const dragEvent = (name: string, init: MouseEventInit): Event => {
    if (typeof W.DragEvent === 'function') {
      return new W.DragEvent(name, { ...init, dataTransfer: transfer ?? undefined });
    }
    // jsdom path: standard MouseEvent with the drag event name still
    // fires the addEventListener(name, …) handler, which is what
    // tests rely on.
    return new MouseEvent(name, init);
  };

  fromEl.dispatchEvent(new MouseEvent('mousedown', opts(fromEl, fromCenter.x, fromCenter.y)));
  fromEl.dispatchEvent(dragEvent('dragstart', opts(fromEl, fromCenter.x, fromCenter.y)));
  await ctx.moveCursor(toCenter, { durationMs: 600 });
  toEl.dispatchEvent(dragEvent('dragover', opts(toEl, toCenter.x, toCenter.y)));
  toEl.dispatchEvent(dragEvent('drop', opts(toEl, toCenter.x, toCenter.y)));
  fromEl.dispatchEvent(dragEvent('dragend', opts(fromEl, toCenter.x, toCenter.y)));
  toEl.dispatchEvent(new MouseEvent('mouseup', opts(toEl, toCenter.x, toCenter.y)));
  await ctx.sleep(350);
}

/**
 * Spotlights a sequence of locators in turn. The current TourPlayer
 * spotlight is single-target; pending the full multi-target visual
 * layer (a separate piece of host work), this implementation cycles
 * the spotlight through each target so the user gets a "tour of
 * related elements" feel. The final target is left highlighted.
 */
async function runMultiSpot(ctx: ActionContext, step: MultiSpotStep): Promise<void> {
  const timeout = step.timeoutMs ?? ctx.defaultTimeoutMs;
  if (step.narrate) ctx.setNarration(step.narrate, 'multi-spot');
  const dwellMs = 500;
  for (let i = 0; i < step.locators.length; i++) {
    const el = await resolve(ctx, step.locators[i], timeout);
    ensureInView(el);
    ctx.setSpotlight(el);
    if (i === 0) await ctx.moveCursor(centerOf(el));
    await ctx.sleep(dwellMs);
  }
}

/**
 * Annotation surfaces the annotation text via {@link ActionContext.setNarration}
 * with a distinct stepType so the host can style it differently. A
 * dedicated annotation overlay layer (sticky-note styled, anchored
 * with placement hints) is host work the language is now ready for.
 */
async function runAnnotate(ctx: ActionContext, step: AnnotateStep): Promise<void> {
  const timeout = step.timeoutMs ?? ctx.defaultTimeoutMs;
  const el = await resolve(ctx, step.locator, timeout);
  await moveAndSpotlight(ctx, el);
  ctx.setNarration(step.text, 'annotate');
  await ctx.sleep(800);
}

async function runExpect(ctx: ActionContext, step: ExpectStep): Promise<void> {
  const a = step.assertion;
  ctx.setNarration(step.narrate ?? `Checking ${a.kind}…`, 'expect');
  const failure = evaluateAssertion(a);
  if (failure === null) return;
  if (a.softFail) {
    // Soft-fail: narrate but do not abort. Acts as a non-blocking
    // assertion useful for diagnostics-without-tour-failure.
    ctx.setNarration(`Soft check failed: ${failure}`, 'expect');
    return;
  }
  throw new AssertionError(failure, { locator: a.locator });
}

/* -------------------------------------------------------------------------- */
/* Dispatcher                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Throwing a HandoffSignal stops the run gracefully and shows the user the
 * provided message. Used by dry-run and `execute: false` steps.
 */
export class HandoffSignal extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'HandoffSignal';
  }
}

async function runChapter(ctx: ActionContext, step: ChapterStep): Promise<void> {
  ctx.setChapter?.(step.title);
}

export async function executeStep(ctx: ActionContext, step: Step): Promise<void> {
  ctx.checkCancelled();
  switch (step.type) {
    case 'narrate': return runNarrate(ctx, step);
    case 'navigate': return runNavigate(ctx, step);
    case 'scrollTo': return runScrollTo(ctx, step);
    case 'move': return runMove(ctx, step);
    case 'hover': return runHover(ctx, step);
    case 'click': return runClick(ctx, step);
    case 'type': return runType(ctx, step);
    case 'press': return runPress(ctx, step);
    case 'select': return runSelect(ctx, step);
    case 'waitFor': return runWaitFor(ctx, step);
    case 'wait': return runWait(ctx, step);
    case 'expect': return runExpect(ctx, step);
    case 'highlight': return runHighlight(ctx, step);
    case 'check':
    case 'uncheck':  return runCheck(ctx, step);
    case 'drag':     return runDrag(ctx, step);
    case 'multi-spot': return runMultiSpot(ctx, step);
    case 'annotate': return runAnnotate(ctx, step);
    case 'chapter': return runChapter(ctx, step);
    default:
      // The Step union is widened to include v2 placeholder kinds whose
      // runtime handlers ship in later phases. Reaching one of those at
      // runtime indicates a validator regression; refuse loudly rather
      // than silently no-op'ing.
      throw new ActionAbortError(
        `step type "${(step as { type: string }).type}" is not supported by this runtime version`,
      );
  }
}

/* -------------------------------------------------------------------------- */
/* Lifecycle wrapper (ETL v2)                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Default backoff applied between retry attempts when a step set
 * `retry: N` but did not specify `backoff:`. Linearly multiplied by the
 * attempt index so the third try waits ~1.5s by default.
 */
const DEFAULT_RETRY_BACKOFF_MS = 500;

/**
 * Wraps {@link executeStep} with the v2 lifecycle: retry loop honoring
 * `step.retry` / `step.backoffMs`, with assertion failures and locator
 * aborts treated as recoverable. Pause / cancel / handoff bubble up
 * unchanged on the first occurrence — never retried.
 *
 * Behavior:
 * - `retry: 0` (default) — single attempt, errors propagate immediately.
 * - `retry: N` — up to N+1 attempts; between attempts, sleep
 *   `backoffMs * attempt` (linear; floor at 100ms).
 * - Soft-fail assertions never throw, so they aren't retried — they're
 *   handled by the executor and surface as narration only.
 *
 * Both v1 and v2 steps go through this wrapper. v1 steps default to
 * `retry: 0`, so they execute exactly once with no behavior change.
 */
export async function executeStepWithLifecycle(ctx: ActionContext, step: Step): Promise<void> {
  const maxRetries = Math.max(0, step.retry ?? 0);
  const baseBackoff = Math.max(0, step.backoffMs ?? DEFAULT_RETRY_BACKOFF_MS);

  for (let attempt = 0; ; attempt++) {
    try {
      await executeStep(ctx, step);
      return;
    } catch (err) {
      // Non-recoverable: propagate immediately.
      if (err instanceof CancellationError) throw err;
      if (err instanceof HandoffSignal) throw err;
      // Recoverable: ActionAbortError (locator missed) and AssertionError.
      const recoverable = err instanceof ActionAbortError || err instanceof AssertionError;
      if (!recoverable) throw err;

      if (attempt >= maxRetries) throw err;

      const wait = Math.max(100, baseBackoff * (attempt + 1));
      ctx.setNarration(
        `Retry ${attempt + 1}/${maxRetries} after ${wait}ms — ${(err as Error).message}`,
        'narrate',
      );
      await ctx.sleep(wait);
    }
  }
}
